import React from 'react';
import {
  Popover, Space, Tag, Tooltip,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import IconFormatter from '../../../utils/iconFormatter';

function typeFormatter(mobile) {
  return mobile ? <Tag color="purple">Мобильное</Tag> : <Tag>Статичное</Tag>;
}

function statusFormatter(disabled) {
  return disabled ? <Tag>Отключено</Tag> : <Tag color="green">Включено</Tag>;
}

const callNumbers = (placename) => String(placename || '').split(',').filter(Boolean).map(Number);

function compareCallNumbers(a, b) {
  const aNums = callNumbers(a.placename);
  const bNums = callNumbers(b.placename);
  for (let i = 0; i < Math.max(aNums.length, bNums.length); i += 1) {
    const diff = (aNums[i] ?? -1) - (bNums[i] ?? -1);
    if (diff !== 0) return diff;
  }
  return 0;
}

function callNumbersFormatter(placename) {
  return callNumbers(placename).map((num, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <Tag key={index}>{num}</Tag>
  ));
}

const VISIBLE_QUEUES = 3;

const queueTagStyle = {
  maxWidth: 180,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

export default function Setting(zones, defaultParams, queues, searchPropsFactory) {
  const connectedQueueNames = (row) => (row.connectedQueues || [])
    .map((id) => queues.find((queue) => queue.id === id))
    .filter(Boolean)
    .map((queue) => queue.name)
    .sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));

  function queuesFormatter(_, row) {
    const names = connectedQueueNames(row);
    if (!names.length) return <Tag>Нет услуг</Tag>;
    if (queues.length && names.length === queues.length) return <Tag color="green">Все услуги</Tag>;
    const hidden = names.slice(VISIBLE_QUEUES);
    return (
      <Space size={[0, 4]} wrap>
        {names.slice(0, VISIBLE_QUEUES).map((name) => (
          <Tooltip key={name} title={name}>
            <Tag style={queueTagStyle}>{name}</Tag>
          </Tooltip>
        ))}
        {hidden.length > 0 && (
          <Popover
            title={`Ещё ${hidden.length}`}
            content={(
              <div style={{ maxHeight: 260, width: 300, overflowY: 'auto' }}>
                {hidden.map((name) => <div key={name}>{name}</div>)}
              </div>
            )}
          >
            <Tag style={{ cursor: 'pointer' }}>{`+${hidden.length}`}</Tag>
          </Popover>
        )}
      </Space>
    );
  }

  // Свои настройки — переопределения параметров, значение которых отличается от базового
  const changedParams = (row) => (row.params || [])
    .map((binding) => {
      const param = defaultParams.find((el) => el.id === binding.officeparam_id);
      return param && String(param.value) !== String(binding.overridevalue) ? param : null;
    })
    .filter(Boolean);

  function settingsFormatter(_, row) {
    const changed = changedParams(row);
    if (!changed.length) return <Tag>Базовые</Tag>;
    return (
      <Tooltip title={(
        <>
          Отличаются от базовых:
          {changed.map((param) => <div key={param.id}>{`• ${param.description}`}</div>)}
        </>
      )}
      >
        <Tag color="blue">Индивидуальные</Tag>
      </Tooltip>
    );
  }

  function zoneFormatter(value) {
    const zone = zones.find((zoneItem) => zoneItem.id === value[0]);
    return (
      <div>{zone ? zone.name : ''}</div>
    );
  }

  return {
    columns: [
      {
        dataIndex: 'id',
        key: 'id',
        title: 'ID',
        sorter: (a, b) => Number(a.id) - Number(b.id),
        defaultSortOrder: 'ascend',
        ...searchPropsFactory('id', 'id'),
      },
      {
        dataIndex: 'name',
        key: 'name',
        title: 'Наименование',
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        ...searchPropsFactory('name', 'Наименование'),
      },
      {
        dataIndex: 'placename',
        key: 'placename',
        title: (
          <Space size={4}>
            Номер для вызова
            <Tooltip title="Номера, которые называются при вызове талона: подставляются в звуковой шаблон вместо WNUM0, WNUM1, WNUM2">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        ),
        sorter: compareCallNumbers,
        ...searchPropsFactory('placename', 'номер для вызова'),
        render: callNumbersFormatter,
      },
      {
        dataIndex: 'connectedZones',
        key: 'connectedZones',
        title: 'Подключенная зона',
        filters: zones.map((zone) => ({ text: zone.name, value: zone.id })),
        filterSearch: true,
        onFilter: (value, row) => (row.connectedZones || []).includes(value),
        render: (_, row) => zoneFormatter(row.connectedZones),
      },
      {
        key: 'connectedQueues',
        title: 'Услуги',
        width: 420,
        sorter: (a, b) => connectedQueueNames(a).length - connectedQueueNames(b).length,
        render: queuesFormatter,
      },
      {
        dataIndex: 'mobile_wplace',
        key: 'mobile_wplace',
        title: 'Тип',
        filters: [
          { text: 'Статичное', value: false },
          { text: 'Мобильное', value: true },
        ],
        onFilter: (value, row) => Boolean(row.mobile_wplace) === value,
        render: typeFormatter,
      },
      {
        dataIndex: 'disabled',
        key: 'disabled',
        title: 'Статус',
        sorter: (a, b) => Number(a.disabled) - Number(b.disabled),
        render: statusFormatter,
      },
      {
        key: 'paramsState',
        title: 'Настройки',
        sorter: (a, b) => changedParams(a).length - changedParams(b).length,
        render: settingsFormatter,
      },
      {
        dataIndex: 'Settings',
        key: 'Settings',
        title: '',
        width: 140,
        render: () => (
        //  IconFormatter({ filter: true, write: true, params: true })
        // фильтер у рабочего места отвечал за показ компонента
        // привязки доп полей к рабочему месту. Рабочее место
          IconFormatter({ write: true, params: true })
        ),
      },
    ],
  };
}
